import type { Editor } from '@tiptap/react';
import { getMarkRange } from '@tiptap/react';

/**
 * Returns the bounding client rect of the inline code mark at the current selection.
 * Used to anchor floating UI (e.g. the whitespace bubble) to the marked text.
 *
 * @param editor - The Tiptap editor instance
 * @returns The rect of the nearest `<code>` element, or a fallback rect from `coordsAtPos`, or null if the selection is not inside a code mark
 */
export function getCodeMarkBoundingRect(editor: Editor | null): DOMRect | null {
    if (!editor || editor.isDestroyed) {
        return null;
    }

    const { schema, doc, selection } = editor.state;
    const codeMark = schema.marks.code;
    if (!codeMark) {
        return null;
    }

    const range = getMarkRange(doc.resolve(selection.from), codeMark);
    if (!range) {
        return null;
    }

    const { from, to } = range;

    try {
        const { node } = editor.view.domAtPos(from + 1);
        const el: Element | null =
            node.nodeType === Node.TEXT_NODE ? node.parentElement : node.nodeType === Node.ELEMENT_NODE ? (node as Element) : null;
        const codeEl = el instanceof Element ? el.closest('code') : null;
        if (codeEl) {
            const rect = codeEl.getBoundingClientRect();
            const fromLeft = editor.view.coordsAtPos(selection.from).left;
            const fromBottom = editor.view.coordsAtPos(selection.from).bottom;
            const toBottom = editor.view.coordsAtPos(to).bottom;
            const toRight = editor.view.coordsAtPos(to).right;
            const yOffset = fromLeft > toRight && toBottom > fromBottom ? 20 : 0;
            return new DOMRect(fromLeft - 70, rect.top - yOffset, rect.right - rect.left, rect.bottom - rect.top);
        }
        const coords = editor.view.coordsAtPos(from);
        return new DOMRect(coords.left, coords.top, coords.right - coords.left, coords.bottom - coords.top);
    } catch {
        return null;
    }
}

/**
 * Returns the document range (from, to) of the inline code mark at the current selection.
 * The range spans the full extent of the mark, even when the selection is collapsed or only partially inside it.
 *
 * @param editor - The Tiptap editor instance
 * @returns The mark's from/to positions, or null if the selection is not inside a code mark
 */
export function getCodeMarkRange(editor: Editor | null): { from: number; to: number } | null {
    if (!editor || editor.isDestroyed) {
        return null;
    }

    const { schema, doc, selection } = editor.state;
    const codeMark = schema.marks.code;
    if (!codeMark) {
        return null;
    }

    return getMarkRange(doc.resolve(selection.from), codeMark) ?? null;
}
